import type {
  DialogFocusOutsideEvent,
  DialogPointerDownOutsideEvent,
  DialogRootProps,
} from "@chakra-ui/react";

/**
 * Chakra UI Modal uses focus trapping; TinyMCE floats dropdowns and dialogs in
 * `.tox-tinymce-aux` on `document.body`. Without this, focusin is swallowed and
 * block/link UI looks disabled or dialogs fail to open.
 * @see https://www.tiny.cloud/blog/tinymce-and-modal-windows/
 */
let refCount = 0;
let handler: ((e: FocusEvent) => void) | undefined;

function onFocusIn(e: FocusEvent): void {
  const el = e.target as HTMLElement | null;
  if (
    el?.closest?.(
      ".tox-tinymce-aux, .tox-dialog, .tox-dialog-wrap, .tox-collection--toolbar, .tox-menu, .tox-collection"
    )
  ) {
    e.stopImmediatePropagation();
  }
}

/** Call once per mounted editor; returns cleanup. */
export function registerTinymceModalFocusFix(): () => void {
  refCount++;
  if (refCount === 1) {
    handler = onFocusIn;
    document.addEventListener("focusin", handler, true);
  }
  return () => {
    refCount--;
    if (refCount <= 0 && handler) {
      document.removeEventListener("focusin", handler, true);
      refCount = 0;
      handler = undefined;
    }
  };
}

const TINYMCE_OUTSIDE_SELECTORS =
  ".tox-tinymce, .tox-tinymce-aux, .tox-dialog, .tox-dialog-wrap, .tox-menu, .tox-collection, .tox-collection--toolbar";

/**
 * True when an Ark/Chakra "outside" event actually targets TinyMCE (iframe body,
 * portaled toolbar UI, or dialogs). Those are not top-layer clicks but the dialog
 * dismiss layer treats them as outside because iframes use another document.
 */
export function isTinymceDialogOutsideEvent(originalEvent: Event): boolean {
  const withPath = originalEvent as Event & {
    composedPath?: () => EventTarget[];
  };
  const path =
    typeof withPath.composedPath === "function"
      ? withPath.composedPath()
      : [];

  for (const node of path) {
    if (!(node instanceof Element)) continue;
    if (node.closest?.(TINYMCE_OUTSIDE_SELECTORS)) return true;
    const od = node.ownerDocument;
    if (od && od !== document) {
      const fe = od.defaultView?.frameElement;
      if (fe instanceof HTMLIFrameElement && fe.closest?.(".tox-tinymce"))
        return true;
    }
  }

  const t = originalEvent.target;
  if (t instanceof HTMLIFrameElement && t.closest?.(".tox-tinymce")) return true;
  if (t && typeof t === "object" && "ownerDocument" in t) {
    const n = t as Node;
    const od =
      n.nodeType === Node.DOCUMENT_NODE ? (n as Document) : n.ownerDocument;
    if (od && od !== document) {
      const fe = od.defaultView?.frameElement;
      if (fe instanceof HTMLIFrameElement && fe.closest?.(".tox-tinymce"))
        return true;
    }
  }
  return false;
}

/** Spread onto `Dialog.Root` next to other props where TinyMCE is used inside the dialog. */
export function getTinymceDialogRootSuppression(): Pick<
  DialogRootProps,
  "onFocusOutside" | "onPointerDownOutside"
> {
  return {
    onFocusOutside: (e: DialogFocusOutsideEvent) => {
      if (isTinymceDialogOutsideEvent(e.detail.originalEvent))
        e.preventDefault();
    },
    onPointerDownOutside: (e: DialogPointerDownOutsideEvent) => {
      if (isTinymceDialogOutsideEvent(e.detail.originalEvent))
        e.preventDefault();
    },
  };
}
